/*
 * Bülowsen – produktside.
 * Variantvalg, galleri, forbindelse, standere og læg i kurv. Læg i kurv sender Dwells
 * CartLinesUpdateEvent, så temaets kurv-skuffe opdateres og åbner som ved temaets egen formular.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const track = (name, data = {}) => window.bwTrack?.(name, data);
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const storage = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* ignorer */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* ignorer */
    }
  },
};

/**
 * Formaterer beløb (i øre) ud fra et eksempel renderet af Liquids money-filter,
 * så markedets valuta, separatorer og placering af valutategn følges.
 */
const createMoneyFormatter = (sample) => {
  const match = typeof sample === 'string' ? sample.match(/1(\D?)234\1?(\D?)56[78](?:(\D)(\d{2}))?/) : null;
  if (!match) return (cents) => (cents / 100).toFixed(2);
  const thousands = match[1] || '';
  const decimal = match[3] || '';
  const decimals = match[4] ? 2 : 0;
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice(match.index + match[0].length);
  return (cents) => {
    const fixed = (cents / 100).toFixed(decimals);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return `${prefix}${fraction ? grouped + decimal + fraction : grouped}${suffix}`;
  };
};

const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template || '');

const joinList = (items, and) => {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
};

const cartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add';
const cartUrl = () => window.Theme?.routes?.cart_url || '/cart';

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart ${response.status}`);
  return response.json();
}

/* Holder headerens kurv-tæller opdateret, uanset hvor kurven ændres. */
if (!window.__bwCartBridge) {
  window.__bwCartBridge = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(async ({ detail }) => {
        const items = detail?.items || (await fetchCart()).items || [];
        const count = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        document.dispatchEvent(new CustomEvent('bw:cart', { detail: { count } }));
      })
      .catch(() => {});
  });
}

/* Produktsektionen */
class BwProduct extends HTMLElement {
  /**
   * Hubrabatter i fast DKK-beløb omregnes i andre valutaer med Shopifys kurs, ligesom checkout gør.
   */
  convertBundleOffers() {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    this.querySelectorAll('[data-bundle-off]').forEach((input) => {
      const price = Math.max(0, Number(input.dataset.basePrice) - Math.round(Number(input.dataset.bundleOff) * rate));
      input.dataset.price = String(price);
      const label = input.closest('label')?.querySelector('[data-bundle-price]');
      if (label) label.textContent = `+ ${this.money(price)}`;
    });
  }

  connectedCallback() {
    const json = this.querySelector('[data-bw-product-json]');
    if (!json) return;
    this.data = JSON.parse(json.textContent);
    this.t = this.data.i18n || {};
    this.money = createMoneyFormatter(this.data.moneySample);
    this.convertBundleOffers();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.form = this.querySelector('[data-bw-product-form]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.groups = Array.from(this.querySelectorAll('[data-option-index]'));
    this.live = this.querySelector('[data-bw-live]');
    this.button = this.querySelector('[data-buy-button]');
    this.buttonLabel = this.querySelector('[data-buy-label]');
    this.error = this.querySelector('[data-buy-error]');
    this.stickyBar = this.querySelector('[data-sticky-bar]');
    this.stickyButton = this.querySelector('[data-sticky-button]');

    const selectedId = Number(this.variantInput?.value);
    this.variant = this.data.variants.find((v) => v.id === selectedId) || this.data.variants[0];

    this.addEventListener('change', (event) => this.onChange(event), { signal });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal });

    this.updateAvailability();
    this.render({ announce: false, pushUrl: false });
    this.setupSticky(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    window.clearTimeout(this.resetTimer);
    window.clearTimeout(this.announceTimer);
  }

  /* Valgte optioner læst fra de afkrydsede radioknapper */
  selectedOptions() {
    return this.groups.map((group) => group.querySelector('input:checked')?.value ?? null);
  }

  findVariant(options) {
    return this.data.variants.find((v) => v.options.every((value, i) => options[i] == null || value === options[i]));
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.classList.contains('bw-pick__input')) {
      const options = this.selectedOptions();
      const group = input.closest('[data-option-index]');
      const index = Number(group.dataset.optionIndex);
      let candidate = this.findVariant(options);
      if (!candidate) {
        /* Kombinationen findes ikke (fx linoleum kun på sortmalet eg): spring til nærmeste variant med den valgte værdi. */
        const fits = (v) => v.options[index] === input.value && v.options.slice(0, index).every((value, i) => value === options[i]);
        candidate =
          this.data.variants.find((v) => fits(v) && v.available) ||
          this.data.variants.find((v) => v.options[index] === input.value && v.available) ||
          this.data.variants.find(fits);
        if (candidate) {
          this.groups.forEach((g, i) => {
            const el = Array.from(g.querySelectorAll('input')).find((x) => x.value === candidate.options[i]);
            if (el) el.checked = true;
          });
        }
      }
      if (!candidate || !candidate.available) {
        /* Utilgængelig kombination: valget fortrydes, og skærmlæseren får besked. */
        this.groups.forEach((g, i) => {
          const el = Array.from(g.querySelectorAll('input')).find((x) => x.value === this.variant.options[i]);
          if (el) el.checked = true;
        });
        this.announce(`${input.value}${this.t.valueUnavailable || ''}`);
        return;
      }
      this.variant = candidate;
      this.updateAvailability();
      this.render({ announce: true, pushUrl: true });
      track('produkt_variant_valgt', { variant_id: candidate.id });
      return;
    }

    if (input.name.startsWith('bw-connection')) {
      this.renderTotals();
      track('produkt_forbindelse_valgt', { forbindelse: input.dataset.connectionKey });
      this.announce(
        input.value ? fill(this.t.announceConnection, { name: input.dataset.name }) : this.t.announceNone || ''
      );
      return;
    }

    if (input.hasAttribute('data-stand-add')) {
      this.renderTotals();
    }
  }

  /* Værdier vurderes mod valgene i de foregående grupper: findes kombinationen ikke, skjules værdien
     (fx linoleumsfronter på andet end sortmalet eg); findes den kun udsolgt, markeres den. */
  updateAvailability() {
    const selected = this.variant.options;
    this.groups.forEach((group, index) => {
      group.querySelectorAll('.bw-pick').forEach((label) => {
        const input = label.querySelector('input');
        const matches = this.data.variants.filter(
          (v) => v.options[index] === input.value && v.options.slice(0, index).every((value, i) => value === selected[i])
        );
        label.hidden = matches.length === 0;
        const unavailable = !matches.some((v) => v.available);
        label.toggleAttribute('data-unavailable', unavailable);
        input.setAttribute('aria-disabled', String(unavailable));
        const text = label.querySelector('[data-unavailable-text]');
        if (text) text.hidden = !unavailable;
      });
    });
  }

  statusFor(variant) {
    const status = this.data.status;
    if (status === 'coming_soon') return { state: 'soon', text: this.t.comingSoon, button: this.t.buttonComingSoon, canBuy: false };
    if (status === 'notify') return { state: 'soon', text: this.t.notify, button: this.t.buttonNotify, canBuy: false };
    if (!variant) return { state: 'soldout', text: this.t.unavailable, button: this.t.buttonUnavailable, canBuy: false };
    if (!variant.available) return { state: 'soldout', text: this.t.soldOut, button: this.t.buttonSoldOut, canBuy: false };
    if (status === 'preorder') return { state: 'preorder', text: this.t.preorder, button: this.t.buttonPreorder, canBuy: true };
    /* 0 på lager, men salg fortsætter: varianten bygges til ordren. */
    if (variant.backorder) return { state: 'order', text: this.t.madeToOrder || this.t.inStock, button: this.t.buttonAdd, canBuy: true };
    return { state: 'instock', text: this.t.inStock, button: this.t.buttonAdd, canBuy: true };
  }

  render({ announce, pushUrl }) {
    const v = this.variant;
    if (!v) return;
    if (this.variantInput) this.variantInput.value = v.id;

    v.options.forEach((value, i) => {
      const legend = this.groups[i]?.querySelector('[data-option-selected]');
      /* Frontværdien "Træ" er samme træ som kabinettet, så træsorten vises i stedet. */
      const shown = i > 0 && value === 'Træ' ? v.options[0] : value;
      if (legend) legend.textContent = shown;
      this.querySelectorAll(`[data-summary-option="${i}"]`).forEach((el) => (el.textContent = shown));
    });

    /* Træ-fronten viser den valgte kabinetfinish (farve, nærbillede og navn). */
    this.querySelectorAll('[data-follow-name]').forEach((el) => (el.textContent = v.options[0]));
    const cabinet = this.groups[0]?.querySelector('input:checked + .bw-pick__swatch');
    if (cabinet) {
      const look = getComputedStyle(cabinet);
      document.documentElement.style.setProperty('--bw-cabinet', look.backgroundColor);
      const image = look.backgroundImage.startsWith('url(') ? look.backgroundImage : 'none';
      document.documentElement.style.setProperty('--bw-cabinet-img', image);
    }

    const price = this.money(v.price);
    this.querySelectorAll('.bw-buy__amount[data-price]').forEach((el) => (el.textContent = price));

    const status = this.statusFor(v);
    this.status = status;
    const statusEl = this.querySelector('[data-status]');
    if (statusEl) {
      statusEl.dataset.state = status.state;
      const text = statusEl.querySelector('[data-status-text]');
      if (text) text.textContent = status.text;
    }
    this.setButton('idle');

    /* Limited Edition */
    this.querySelectorAll('[data-edition-badge]').forEach((el) => {
      el.hidden = !v.limited;
      el.textContent = v.badge;
    });
    const card = this.querySelector('[data-edition-card]');
    if (card) {
      card.hidden = !v.limited;
      if (v.limited) {
        card.querySelector('[data-edition-name]').textContent = v.options[v.options.length - 1];
        card.querySelector('[data-edition-badge-card]').textContent = v.badge;
        card.querySelector('[data-edition-material]').textContent = v.material;
        card.querySelector('[data-edition-lead]').textContent = v.lead;
      }
    }
    const product = this.querySelector('[data-summary-product]');
    if (product) product.textContent = v.limited ? `${product.dataset.base}, ${v.badge}` : product.dataset.base;

    const sticky = this.querySelector('[data-sticky-variant]');
    if (sticky) sticky.textContent = v.options[0];

    this.renderTotals();

    if (pushUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    document.dispatchEvent(
      new CustomEvent('bw:variant', { detail: { productId: this.data.id, variant: v, sectionId: this.dataset.sectionId } })
    );

    if (announce) {
      const [first = '', second = ''] = v.options;
      const lead = v.options.length > 1 ? fill(this.t.announceVariant, { 1: first, 2: second }) : `${first}.`;
      this.announce(`${lead} ${status.text}.`);
    }
  }

  /* Samlet pris, opsummering og noten om linjevarer */
  renderTotals() {
    let total = this.variant?.price || 0;
    const extras = [];

    const connection = this.querySelector('input[name^="bw-connection"]:checked');
    if (connection?.value) {
      total += Number(connection.dataset.price) || 0;
      extras.push(connection.dataset.name);
    }
    const summaryConnection = this.querySelector('[data-summary-connection]');
    if (summaryConnection && connection) summaryConnection.textContent = connection.dataset.name;

    const stand = this.querySelector('[data-stand-add]');
    const accessoryRow = this.querySelector('[data-summary-accessory]');
    if (stand?.checked) {
      total += Number(stand.dataset.price) || 0;
      extras.push(stand.dataset.name);
    }
    if (accessoryRow) accessoryRow.hidden = !stand?.checked;

    const totalText = this.money(total);
    this.querySelectorAll('[data-total], [data-sticky-price]').forEach((el) => (el.textContent = totalText));

    const note = this.querySelector('[data-buy-note]');
    if (note) {
      note.textContent = extras.length
        ? fill(this.t.noteSeparate, { items: joinList([this.t.setName, ...extras], this.t.and) })
        : this.t.noteNone;
    }
  }

  setButton(state) {
    if (!this.button) return;
    const status = this.status || this.statusFor(this.variant);
    const labels = { idle: status.button, adding: this.t.buttonAdding, added: this.t.buttonAdded };
    const label = labels[state] || status.button;
    if (this.buttonLabel) this.buttonLabel.textContent = label;
    this.button.disabled = state === 'adding' || !status.canBuy;
    if (state === 'adding') this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
    if (this.stickyButton) {
      this.stickyButton.textContent = label;
      this.stickyButton.disabled = this.button.disabled;
    }
    this.updateSticky();
  }

  /* Tøm først, skriv derefter, så samme tekst også annonceres to gange i træk. */
  announce(text) {
    if (!this.live) return;
    this.live.textContent = '';
    window.clearTimeout(this.announceTimer);
    this.announceTimer = window.setTimeout(() => {
      this.live.textContent = text;
    }, 60);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  async onSubmit(event) {
    event.preventDefault();
    const status = this.statusFor(this.variant);
    if (!status.canBuy || this.button?.getAttribute('aria-busy') === 'true') return;

    const items = [{ id: this.variant.id, quantity: 1 }];
    const connection = this.querySelector('input[name^="bw-connection"]:checked');
    if (connection?.value) items.push({ id: Number(connection.value), quantity: 1 });
    const stand = this.querySelector('[data-stand-add]');
    if (stand?.checked) items.push({ id: Number(stand.value), quantity: 1 });

    this.showError('');
    this.setButton('adding');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (result.status) {
        const cart = await fetchCart().catch(() => null);
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'bw-product', sourceId: this.dataset.sectionId, itemCount: items.length, productId: String(this.data.id) },
          });
        } else {
          deferred.reject(new Error(result.message));
        }
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description, errors: result.errors } }));
        this.showError(result.description || result.message || this.t.cartError);
        this.setButton('idle');
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bw-product',
          sourceId: this.dataset.sectionId,
          itemCount: items.length,
          productId: String(this.data.id),
          sections: result.sections,
          didError: false,
        },
      });
      track('produkt_laeg_i_kurv', { lines: items });
      this.setButton('added');
      this.announce(this.t.buttonAdded);
      window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => this.setButton('idle'), 2400);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
      this.showError(this.t.cartError);
      this.setButton('idle');
    }
  }

  /* Sticky købslinje: vises, når købsknappen er scrollet ud over toppen, og kun ved gyldigt valg. */
  setupSticky(signal) {
    if (!this.stickyBar || !this.button || !('IntersectionObserver' in window)) return;
    this.buttonOutOfView = false;
    this.observer = new IntersectionObserver(([entry]) => {
      this.buttonOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.updateSticky();
    });
    this.observer.observe(this.button);
    signal.addEventListener('abort', () => this.observer?.disconnect());
  }

  updateSticky() {
    if (!this.stickyBar) return;
    const valid = Boolean(this.status?.canBuy);
    this.stickyBar.hidden = !(this.buttonOutOfView && valid);
  }
}

/* Galleri: scroll-snap, miniaturer, piletaster, Enter zoomer, variantens billede først. */
class BwGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.caption = this.querySelector('[data-gallery-caption]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.index = Number(this.dataset.start) || 0;
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)), { signal }));
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openZoom(), { signal });
    this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog?.close(), { signal });
    this.dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.dialog.close();
      },
      { signal }
    );

    this.track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(Math.min(this.index + 1, this.slides.length - 1));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(Math.max(this.index - 1, 0));
        } else if (event.key === 'Enter') {
          event.preventDefault();
          this.openZoom();
        }
      },
      { signal }
    );

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const width = this.track.clientWidth || 1;
          const index = Math.round(this.track.scrollLeft / width);
          if (index !== this.index) this.setActive(index);
        });
      },
      { passive: true, signal }
    );

    document.addEventListener(
      'bw:variant',
      (event) => {
        const product = this.closest('bw-product');
        if (product && event.detail.sectionId !== product.dataset.sectionId) return;
        const mediaId = String(event.detail.variant.media || '');
        // Varianter uden eget billede viser galleriets første billede i stedet for det forrige.
        const index = mediaId && mediaId !== '0' ? this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId) : 0;
        if (index > -1 && index !== this.index) this.goTo(index);
      },
      { signal }
    );

    if (this.index > 0) this.goTo(this.index, false);
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  goTo(index, smooth = true) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
    this.setActive(index);
  }

  setActive(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.caption) this.caption.textContent = this.slides[index]?.dataset.alt || '';
  }

  openZoom() {
    if (!this.dialog) return;
    const slide = this.slides[this.index];
    const img = this.dialog.querySelector('[data-gallery-zoom-img]');
    if (img && slide) {
      img.src = slide.dataset.zoom;
      img.alt = slide.querySelector('img')?.alt || '';
    }
    this.dialog.showModal();
    this.dialog.querySelector('[data-gallery-close]')?.focus();
  }
}

/* Standermodul: venteliste med samtykke via Shopifys kundeformular. */
class BwStand extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.panel = this.querySelector('[data-stand-panel]');
    this.intro = this.querySelector('[data-stand-intro]');
    this.openButton = this.querySelector('[data-stand-open]');
    this.status = this.querySelector('[data-stand-status]');
    this.fields = this.querySelector('[data-stand-fields]');
    this.email = this.querySelector('[data-stand-email]');
    this.consent = this.querySelector('[data-stand-consent]');
    this.submit = this.querySelector('[data-stand-submit]');
    if (!this.form || !this.panel) return;
    this.controller = new AbortController();
    const { signal } = this.controller;

    /* Sidens kundeformularer (venteliste og nyhedsbrev) deler Shopifys svar. Kun et svar på
       netop denne formular vises her. */
    const key = this.dataset.formKey;
    let state = this.status?.dataset.state || '';
    if (state && storage.get('bw-last-form') !== key) state = '';
    if (state === 'success') {
      track('stander_tilmeldt');
      storage.remove('bw-last-form');
    }

    if (state === 'success') {
      if (this.fields) this.fields.hidden = true;
      if (this.intro) this.intro.hidden = true;
      this.panel.hidden = false;
    } else if (state === 'error') {
      this.panel.hidden = false;
      if (this.intro) this.intro.hidden = true;
      this.email?.setAttribute('aria-invalid', 'true');
    } else {
      if (this.status) {
        this.status.textContent = '';
        delete this.status.dataset.state;
      }
      this.panel.hidden = true;
      if (this.intro) this.intro.hidden = false;
    }

    this.form.noValidate = true;
    this.openButton?.addEventListener(
      'click',
      () => {
        const open = this.openButton.getAttribute('aria-expanded') === 'true';
        this.openButton.setAttribute('aria-expanded', String(!open));
        this.panel.hidden = open;
        if (!open) this.email?.focus();
      },
      { signal }
    );

    this.form.addEventListener(
      'submit',
      (event) => {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.email?.value.trim() || '');
        const consentOk = Boolean(this.consent?.checked);
        if (emailOk) this.email?.removeAttribute('aria-invalid');
        else this.email?.setAttribute('aria-invalid', 'true');
        if (emailOk && !consentOk) this.consent?.setAttribute('aria-invalid', 'true');
        else this.consent?.removeAttribute('aria-invalid');
        if (!emailOk || !consentOk) {
          event.preventDefault();
          this.setStatus('error', !emailOk ? this.dataset.errorEmail : this.dataset.errorConsent);
          (!emailOk ? this.email : this.consent)?.focus();
          return;
        }
        storage.set('bw-last-form', key);
        this.setStatus('sending', this.dataset.sending);
        if (this.submit) {
          this.submit.textContent = this.dataset.sending;
          this.submit.setAttribute('aria-busy', 'true');
        }
      },
      { signal }
    );
  }

  setStatus(state, text) {
    if (!this.status) return;
    this.status.dataset.state = state;
    this.status.textContent = text || '';
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/* Materialer og finish: viser det valgte kabinet og den valgte front. */
class BwMaterials extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    document.addEventListener('bw:variant', (event) => this.update(event.detail.variant), { signal: this.controller.signal });
    this.followCabinet();
  }

  /* Træ-fronten viser samme træ som kabinettet: kopier kabinettets nærbillede over. */
  followCabinet() {
    const front = this.querySelector('[data-material-follow]:not([hidden])');
    if (!front) return;
    front.querySelector('img')?.remove();
    const source = this.querySelector('[data-material-option="0"] [data-material-value]:not([hidden]) img');
    if (source) front.append(source.cloneNode());
  }

  update(variant) {
    if (!variant) return;
    this.querySelectorAll('[data-material-option]').forEach((pane) => {
      const value = variant.options[Number(pane.dataset.materialOption)];
      pane.querySelectorAll('[data-material-value]').forEach((item) => {
        item.hidden = item.dataset.materialValue !== value;
      });
      const label = pane.querySelector('[data-material-label]');
      if (label && value) label.textContent = pane.dataset.materialOption !== '0' && value === 'Træ' ? variant.options[0] : value;
    });
    this.followCabinet();
    const limited = this.querySelector('[data-material-limited]');
    if (limited) {
      limited.hidden = !variant.limited || !variant.material;
      if (variant.material) limited.textContent = variant.material;
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

const define = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};
define('bw-gallery', BwGallery);
define('bw-stand', BwStand);
define('bw-materials', BwMaterials);
define('bw-product', BwProduct);
